'use client';
import {
  Fragment,
  useRef,
  FC,
  ReactNode,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import {
  Transition,
  Dialog,
  TransitionChild,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

import { Svg } from '@/components';
import { useCart } from '@/context/cartContext';
import CloseIcon from '@/icons/CloseIcon';
import { X } from 'lucide-react';
import { useUser } from '@/context/userContext';
import { getKeyValue } from '@/utils/helper';
import { storage } from '@/config';
import { GURUGRAM_STORE_CODE } from '@/constants';

type ClassesTypes = {
  panel?: string;
};

interface PropTypes {
  id: string;
  onClose: (...args: any) => void;
  show: boolean;
  children: ReactNode;
  classes?: ClassesTypes;
}

const CartSidebarLayout: FC<PropTypes> = ({
  id,
  show = false,
  onClose,
  children,
  classes,
}) => {
  const [closeNudge, setCloseNudge] = useState(false);
  const focusRef = useRef(null);
  const { cartItemsCount } = useCart();
  const { userDetails } = useUser();
  const isGuest = !storage.get('token');
  const attributes =
    userDetails?.custom_attributes &&
    getKeyValue(userDetails?.custom_attributes, 'attribute_code');
  const newUser = attributes?.user_type === 'new';
  const isGGNStore = storage.get('store_code') === GURUGRAM_STORE_CODE; // Need to find alternative for this hardcode check
  const freeShippingNudge = newUser && isGGNStore;
  const showFreeShippingNudge =
    !closeNudge &&
    !!cartItemsCount &&
    cartItemsCount > 0 &&
    ((isGGNStore && isGuest) || freeShippingNudge);

  useEffect(() => {
    setCloseNudge(false);
  }, [cartItemsCount]);

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-30'
        onClose={onClose}
        initialFocus={focusRef}
        aria-label={id}
        aria-labelledby={id}
        aria-describedby={id}>
        <TransitionChild
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div className='fixed inset-0 bg-gray-900/70' />
        </TransitionChild>

        <div className='fixed inset-y-0 right-0 h-dvh w-full md:w-[90%] lg:w-1/2 xl:w-1/3 min-[2559px]:w-[22%]'>
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-x-full'
            enterTo='opacity-100 -translate-x-0'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 -translate-x-0'
            leaveTo='opacity-0 translate-x-full'>
            <DialogPanel
              className={twMerge(
                `h-full max-w-full transform bg-dew text-left transition-all flex flex-col`,
                classes?.panel
              )}>
              <div>
                <div
                  aria-label='cart-header'
                  className='flex items-center justify-between gap-3 bg-white px-2 py-3'>
                  <DialogTitle
                    as='h3'
                    className='px-1 text-lg font-semibold md:text-2xl relative'>
                    {cartItemsCount ? (
                      <>
                        Cart ({cartItemsCount} Item
                        {cartItemsCount > 1 ? 's' : ''})
                      </>
                    ) : (
                      'Cart'
                    )}
                  </DialogTitle>
                  <button type='button' onClick={onClose} className='px-3'>
                    <Svg className='stroke-fig'>
                      <CloseIcon />
                    </Svg>
                  </button>
                </div>
                {showFreeShippingNudge && (
                  <Nudge setCloseNudge={setCloseNudge} />
                )}
              </div>

              <div aria-label='cart-body' className='flex-1 overflow-y-auto'>
                {children}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CartSidebarLayout;

function Nudge(props: { setCloseNudge: Dispatch<SetStateAction<boolean>> }) {
  const { setCloseNudge } = props;
  return (
    <div
      style={{
        backgroundImage: 'url(/images/free-ship-bg.svg)',
      }}
      className='h-8 flex items-center justify-center gap-3 text-xs font-normal text-[#337052]'>
      <img src={'/images/free-ship-joy.svg'} alt='icon' />
      <p>
        <b className='font-semibold'>Free shipping</b> on your first order!
      </p>
      <X
        className='cursor-pointer'
        size={12}
        onClick={() => {
          setCloseNudge(true);
        }}
      />
    </div>
  );
}
